import { io } from 'socket.io-client';
import { SOCKET_URL } from '../config';

class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.tenantInfo = null;
    this.authData = null;
    // Set true while WE intentionally tear down the socket (disconnect()/logout)
    // so the 'disconnect' handler can tell an EXPECTED close from an unexpected
    // drop and only fire onUnexpectedDisconnect for the latter.
    this.intentionalDisconnect = false;
    // App-supplied callback fired when an ESTABLISHED socket drops on its own
    // (network blip, server restart) — drives the auto-reconnect modal.
    this.onUnexpectedDisconnect = null;
    // Set true when the server emits 'server-restarting' just before a deploy
    // closes our socket. Lets the ensuing disconnect be treated as EXPECTED
    // maintenance (calm "back in a moment" copy + reconnect that waits for the
    // server to come back) rather than an alarming network failure. Cleared on
    // the next successful connect.
    this.maintenanceExpected = false;
    // App-supplied callback fired the moment 'server-restarting' arrives, so the
    // UI can switch to maintenance messaging before the socket even drops.
    this.onServerRestarting = null;

    // --- Outbound message queue ------------------------------------------
    // Messages the user sends while the socket is down (a blip mid-sentence,
    // a server restart) are buffered here instead of being silently dropped,
    // then flushed in order on the next successful 'connect'. Bounded so a
    // long outage can't grow it without limit — oldest entries are evicted.
    this.pendingMessages = [];
    this.MAX_PENDING = 50;
    // App-supplied callback fired whenever the queue length changes, so the UI
    // can show "N message(s) will send when reconnected".
    this.onPendingChange = null;
  }

  /** Number of outbound messages currently waiting for reconnection. */
  get pendingCount() {
    return this.pendingMessages.length;
  }

  /** Notify the app that the pending-queue length changed. */
  _notifyPending() {
    if (typeof this.onPendingChange === 'function') {
      this.onPendingChange(this.pendingMessages.length);
    }
  }

  /**
   * Public flush entry point. Call this once the room join has been confirmed
   * (server-side socket.currentRoom is set) so flushed messages aren't rejected
   * with "Not in a room". Safe to call when the queue is empty or the socket is
   * down (no-op / leaves the queue intact for the next reconnect).
   */
  flushPending() {
    this._flushPending();
  }

  /**
   * Flush every buffered outbound message in FIFO order. If the socket drops
   * again mid-flush, the unsent remainder is left in the queue for the next
   * reconnect.
   */
  _flushPending() {
    if (!this.pendingMessages.length) return;
    console.log(`📤 Flushing ${this.pendingMessages.length} queued message(s)`);
    // Drain a snapshot so re-queues (from a mid-flush drop) don't loop forever.
    const queued = this.pendingMessages;
    this.pendingMessages = [];
    this._notifyPending();
    for (const { content, type } of queued) {
      if (!this.isConnected || !this.socket) {
        // Socket dropped again — put this and the rest back, preserving order.
        this.pendingMessages.push({ content, type });
        continue;
      }
      this.socket.emit('send-message', { content, type });
    }
    if (this.pendingMessages.length) this._notifyPending();
  }

  /**
   * Connect to tenant-specific Socket.IO namespace
   * @param {Object} tenantInfo - Tenant information with tenantId and scriptHash
   * @param {Object} authData - Authentication data with token
   * @returns {Promise<void>}
   */
  async connect(tenantInfo, authData) {
    if (this.isConnected) {
      console.warn('Socket already connected');
      return;
    }

    // Never orphan an in-flight socket. isConnected only flips on 'connect', so
    // two overlapping connect() calls both pass the guard above — the second
    // would overwrite this.socket while the first keeps handshaking in the
    // void, leaving a ZOMBIE second connection for the same user that the
    // server then displaces (the post-deploy "two connect cycles" burst /
    // "connected from another location" loop). Tear any prior socket down
    // before creating a new one.
    if (this.socket) {
      console.warn('🧟 Prior socket still present before connect — closing it');
      try {
        this.socket.removeAllListeners();
        this.socket.close();
      } catch (e) { /* already dead — nothing to close */ }
      this.socket = null;
    }

    this.tenantInfo = tenantInfo;
    this.authData = authData;

    const namespace = `/tenant/${tenantInfo.tenantId}/${tenantInfo.scriptHash}`;
    const serverUrl = SOCKET_URL;

    console.log(`🔌 Connecting to socket namespace: ${namespace}`);

    // reconnection: false — we own the retry logic (backoff + modal) in App, so
    // socket.io's built-in reconnection is disabled to avoid two competing
    // loops. A drop fires our 'disconnect' handler → onUnexpectedDisconnect.
    this.socket = io(`${serverUrl}${namespace}`, {
      auth: {
        token: authData.token,
        tenantId: tenantInfo.tenantId,
        scriptHash: tenantInfo.scriptHash
      },
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: false
    });
    this.intentionalDisconnect = false;

    return new Promise((resolve, reject) => {
      this.socket.on('connect', () => {
        console.log('✅ Connected to chat socket');
        this.isConnected = true;
        // Dev-only debug handle so the rtc:* signaling path can be exercised
        // from the console / automated preview. No-op in production builds.
        if (process.env.NODE_ENV !== 'production') {
          window.__chatSocket = this.socket;
        }
        // A fresh, healthy connection — the maintenance window (if any) is over.
        this.maintenanceExpected = false;
        // NOTE: queued messages are NOT flushed here. They must wait until the
        // server has confirmed the room join (socket.currentRoom set), else the
        // server rejects them with "Not in a room". The App calls flushPending()
        // explicitly after joinChat() acks — see connectToChat().
        resolve();
      });

      // The server is about to restart (deploy). Flag the imminent disconnect as
      // EXPECTED maintenance so it's handled calmly, and let the App switch
      // messaging immediately (before the socket actually drops).
      this.socket.on('server-restarting', (info) => {
        console.log('🛠️  Server restarting — expecting a brief reconnect', info);
        this.maintenanceExpected = true;
        if (typeof this.onServerRestarting === 'function') {
          this.onServerRestarting(info || {});
        }
      });

      this.socket.on('connect_error', (error) => {
        console.error('❌ Socket connection error:', error);
        this.isConnected = false;
        reject(error);
      });

      this.socket.on('disconnect', (reason) => {
        console.log('🔌 Socket disconnected:', reason);
        const wasConnected = this.isConnected;
        this.isConnected = false;
        // An UNEXPECTED drop: we were connected and didn't ask for it. Notify App
        // so it can start the auto-reconnect backoff. Skip when WE initiated the
        // close (disconnect()/logout) — that's expected teardown.
        //
        // Reasons we care about:
        //  - A deploy's io.close() arrives as 'transport close' (verified on
        //    socket.io 4.8) — already reconnect-eligible. The maintenanceExpected
        //    flag (set by the preceding 'server-restarting') just tells the App to
        //    show calm "updating" copy and wait for the server before retrying.
        //  - 'io server disconnect' means the server sent an explicit DISCONNECT
        //    packet — today that's displacement (owned by the onDisplaced flow), so
        //    we DON'T reconnect on it. The `|| maintenanceExpected` keeps us correct
        //    if a future server ever closes deploy sockets that way instead.
        if (
          wasConnected &&
          !this.intentionalDisconnect &&
          (reason !== 'io server disconnect' || this.maintenanceExpected) &&
          typeof this.onUnexpectedDisconnect === 'function'
        ) {
          this.onUnexpectedDisconnect(reason, { maintenance: this.maintenanceExpected });
        }
      });

      // (Heartbeat is handled by Engine.IO's built-in transport-level ping/pong
      // — the socket.io client answers those automatically. The former
      // app-level 'ping'/'pong' handshake was removed with its server half.)

      // Set connection timeout
      setTimeout(() => {
        if (!this.isConnected) {
          reject(new Error('Socket connection timeout'));
        }
      }, 10000);
    });
  }

  /**
   * Disconnect from socket
   * @returns {Promise<void>}
   */
  async disconnect() {
    if (this.socket) {
      console.log('🔌 Disconnecting from socket');
      // Mark this as an EXPECTED close so the 'disconnect' handler doesn't fire
      // onUnexpectedDisconnect (which would wrongly trigger auto-reconnect).
      this.intentionalDisconnect = true;
      this.maintenanceExpected = false;
      // An intentional close ends this session — discard any queued outbound
      // messages so they can't leak into a later/different session on reconnect.
      if (this.pendingMessages.length) {
        this.pendingMessages = [];
        this._notifyPending();
      }

      return new Promise((resolve) => {
        // If already disconnected, resolve immediately
        if (!this.isConnected) {
          this.socket = null;
          this.tenantInfo = null;
          this.authData = null;
          resolve();
          return;
        }

        // Wait for disconnect confirmation
        const timeout = setTimeout(() => {
          console.log('🔌 Disconnect timeout - forcing cleanup');
          this.socket = null;
          this.isConnected = false;
          this.tenantInfo = null;
          this.authData = null;
          resolve();
        }, 2000); // 2 second timeout

        this.socket.once('disconnect', () => {
          clearTimeout(timeout);
          console.log('🔌 Socket disconnected confirmed');
          this.socket = null;
          this.isConnected = false;
          this.tenantInfo = null;
          this.authData = null;
          resolve();
        });

        // Initiate disconnect
        this.socket.disconnect();
      });
    }
  }

  /**
   * Join chat room and receive welcome message
   * @param {string} roomName - Optional room name (defaults to instance default)
   */
  joinChat(roomName = null) {
    if (!this.isConnected || !this.socket) {
      throw new Error('Socket not connected');
    }

    console.log(`👋 Joining chat room: ${roomName || 'default'}`);
    // Returns a Promise that resolves once the server confirms the join (room
    // is set server-side), so callers can safely start room-scoped flows like
    // WebRTC media signaling. Resolves (not rejects) on timeout to stay
    // non-blocking for text chat if the ack is somehow lost.
    return new Promise((resolve) => {
      let settled = false;
      const done = (v) => { if (!settled) { settled = true; resolve(v); } };
      this.socket.emit('join-chat', { roomName }, (ack) => done(ack || { joined: true }));
      setTimeout(() => done({ joined: false, timedOut: true }), 5000);
    });
  }

  /**
   * Send a message to the current room
   * @param {string} content - Message content
   * @param {string} type - Message type (TEXT, IMAGE, etc.)
   */
  sendMessage(content, type = 'TEXT') {
    if (!content || content.trim().length === 0) {
      throw new Error('Message content is required');
    }

    const payload = { content: content.trim(), type };

    // Socket is down — queue instead of dropping the message. It flushes in
    // order once the room join is re-confirmed (see flushPending). The caller
    // can read pendingCount (or hook onPendingChange) to tell the user their
    // text is held, not lost.
    if (!this.isConnected || !this.socket) {
      // Only TEXT is queueable. An IMAGE's content is a URL that only exists
      // because the file was uploaded over a LIVE connection — there's no valid
      // IMAGE to send while offline, so the server would reject a flushed one.
      // Don't pretend it's held.
      if (type !== 'TEXT') {
        console.warn(`📭 Socket down — dropping non-text (${type}) message, not queueable`);
        return { queued: false, dropped: true };
      }
      if (this.pendingMessages.length >= this.MAX_PENDING) {
        // Bounded queue: drop the OLDEST so a long outage can't grow it without
        // limit while still keeping the user's most recent intent.
        this.pendingMessages.shift();
      }
      this.pendingMessages.push(payload);
      this._notifyPending();
      console.warn(`📥 Socket down — queued message (${this.pendingMessages.length} pending)`);
      return { queued: true };
    }

    this.socket.emit('send-message', payload);
    return { queued: false };
  }

  /**
   * Send a private message to a specific user
   * @param {string} content - Message content
   * @param {string} targetUserId - Target user ID
   * @param {string} type - Message type (TEXT, etc.)
   */
  sendPrivateMessage(content, targetUserId, type = 'TEXT') {
    if (!this.isConnected || !this.socket) {
      throw new Error('Socket not connected');
    }

    if (!content || content.trim().length === 0) {
      throw new Error('Message content is required');
    }

    if (!targetUserId) {
      throw new Error('Target user ID is required');
    }

    this.socket.emit('send-private', {
      content: content.trim(),
      targetUserId,
      type
    });
  }

  /**
   * Private (IM-window) typing indicator — relayed only to the one target peer
   * (vs. the room-wide startTyping/stopTyping). Best-effort; silently no-ops
   * when disconnected.
   * @param {string} targetUserId
   * @param {boolean} isTyping
   */
  sendPrivateTyping(targetUserId, isTyping) {
    if (this.isConnected && this.socket && targetUserId) {
      this.socket.emit('private-typing', { targetUserId, isTyping: !!isTyping });
    }
  }

  /**
   * Set (or clear) an AIM-style away message. The server holds it for the
   * session and auto-replies once per sender to anyone who IMs you while away.
   * Pass an empty/blank message (or call clearAway) to come back.
   * @param {string} message
   */
  setAway(message) {
    if (this.isConnected && this.socket) {
      this.socket.emit('set-away', { message: message || '' });
    }
  }

  clearAway() {
    if (this.isConnected && this.socket) {
      this.socket.emit('clear-away');
    }
  }

  /**
   * Persist the user's chosen chat text color on their workspace membership.
   * @param {string} color - #RGB or #RRGGBB hex color
   */
  setColor(color) {
    if (this.isConnected && this.socket) {
      this.socket.emit('set-color', { color });
    }
  }

  /**
   * Persist the user's chosen USERNAME (display-name) color on their workspace
   * membership. Independent of the message text color above. The server
   * enforces the admin-reserved color band and rejects invalid colors.
   * @param {string} color - #RGB or #RRGGBB hex color
   */
  setUsernameColor(color) {
    if (this.isConnected && this.socket) {
      this.socket.emit('set-username-color', { color });
    }
  }

  /**
   * Persist the user's chosen chat display mode on their workspace membership.
   * @param {string} viewMode - 'default' | 'compact' | 'bubble'
   */
  setViewMode(viewMode) {
    if (this.isConnected && this.socket) {
      this.socket.emit('set-view-mode', { viewMode });
    }
  }

  /**
   * Persist the user's chosen chat visual theme on their workspace membership.
   * A per-user override of the tenant default. Best-effort: a disconnected
   * socket is a no-op (the choice is still applied locally + mirrored to
   * localStorage by the caller).
   * @param {string} theme - 'midnight' | 'sesh'
   */
  setTheme(theme) {
    if (this.isConnected && this.socket) {
      this.socket.emit('set-theme', { theme });
    }
  }

  /**
   * Persist the user's chosen avatar on their GLOBAL account. The url must be a
   * backend upload path ("/uploads/<instanceId>/<hex>.<ext>") returned by the
   * upload/image API; the server re-validates the shape before storing.
   * @param {string} avatarUrl - relative upload path
   */
  setAvatar(avatarUrl) {
    if (this.isConnected && this.socket) {
      this.socket.emit('set-avatar', { avatarUrl });
    }
  }

  /**
   * Persist the user's profile bio on their workspace membership. Best-effort (a
   * disconnected socket is a no-op — applied locally by the caller). The server
   * strips control chars and caps at 200 chars; empty clears the bio.
   * @param {string} bio
   */
  setBio(bio) {
    if (this.isConnected && this.socket) {
      this.socket.emit('set-bio', { bio });
    }
  }

  /**
   * Persist the user's profile BANNER color (solid hero background) on their
   * workspace membership. Best-effort. The server validates '#rrggbb'; empty/null
   * clears it (banner falls back to the theme default).
   * @param {string|null} color - '#rrggbb' hex, or '' / null to clear
   */
  setBannerColor(color) {
    if (this.isConnected && this.socket) {
      this.socket.emit('set-banner-color', { color });
    }
  }

  /**
   * Persist the user's mention-notification sound preference on their workspace
   * membership. A per-user setting; best-effort (a disconnected socket is a
   * no-op — the choice is still applied locally + mirrored to localStorage by
   * the caller). The server validates against its allowlist.
   * @param {string} mentionSound - 'chime' | 'off'
   */
  setMentionSound(mentionSound) {
    if (this.isConnected && this.socket) {
      this.socket.emit('set-mention-sound', { mentionSound });
    }
  }

  /**
   * Persist the mention-sound volume (0-100) on the membership. Best-effort; a
   * disconnected socket is a no-op (the choice is applied locally + mirrored to
   * localStorage by the caller). The server clamps/validates the range.
   * @param {number} mentionVolume - integer 0-100
   */
  setMentionVolume(mentionVolume) {
    if (this.isConnected && this.socket) {
      this.socket.emit('set-mention-volume', { mentionVolume });
    }
  }

  /**
   * Persist the chat message font size on the membership. Best-effort (a
   * disconnected socket is a no-op — applied locally + mirrored by the caller).
   * @param {string} fontSize - 'small' | 'medium' | 'large'
   */
  setFontSize(fontSize) {
    if (this.isConnected && this.socket) {
      this.socket.emit('set-font-size', { fontSize });
    }
  }

  /**
   * Persist the message timestamp format on the membership. Best-effort (a
   * disconnected socket is a no-op — applied locally + mirrored by the caller).
   * @param {string} timestampFormat - 'relative' | 'clock' | 'off'
   */
  setTimestampFormat(timestampFormat) {
    if (this.isConnected && this.socket) {
      this.socket.emit('set-timestamp-format', { timestampFormat });
    }
  }

  /**
   * TEMP DIAGNOSTIC: forward a client-side debug line to the backend so it shows
   * in the server logs (docker logs cc-backend). Used to debug the iOS camera
   * front↔back swap path on a remote device whose console we can't see directly.
   * Best-effort, no ack. Remove once the swap bug is pinned down.
   * @param {string} tag   - short event tag, e.g. 'ios-swap'
   * @param {object} data  - small JSON-serializable payload
   */
  clientDebugLog(tag, data = {}) {
    if (this.isConnected && this.socket) {
      this.socket.emit('client-debug-log', { tag, data });
    }
  }

  // =====================================================================
  // WebRTC (mediasoup SFU) signaling — rtc:* emit/ack wrappers.
  // Used by mediaService.js. Each returns a Promise that resolves with the
  // server ack payload, or rejects if the ack carries an { error }.
  // =====================================================================

  /** Generic emit-with-ack helper for rtc:* events. */
  rtcRequest(event, payload = {}) {
    return new Promise((resolve, reject) => {
      if (!this.isConnected || !this.socket) {
        return reject(new Error('Socket not connected'));
      }
      this.socket.emit(event, payload, (ack) => {
        if (ack && ack.error) return reject(new Error(ack.error));
        resolve(ack || {});
      });
    });
  }

  getRtpCapabilities() {
    return this.rtcRequest('rtc:get-rtp-capabilities');
  }

  createTransport(direction) {
    return this.rtcRequest('rtc:create-transport', { direction });
  }

  connectTransport(transportId, dtlsParameters) {
    return this.rtcRequest('rtc:connect-transport', { transportId, dtlsParameters });
  }

  produce(transportId, kind, rtpParameters, outputQuality) {
    return this.rtcRequest('rtc:produce', { transportId, kind, rtpParameters, outputQuality });
  }

  consume(transportId, producerId, rtpCapabilities, desiredQuality) {
    return this.rtcRequest('rtc:consume', { transportId, producerId, rtpCapabilities, desiredQuality });
  }

  resumeConsumer(consumerId) {
    return this.rtcRequest('rtc:resume-consumer', { consumerId });
  }

  // Black-feed recovery: ask the SFU to re-request a keyframe from the producer
  // for one of OUR consumers (used by mediaService's no-frames watchdog when a
  // freshly-consumed video decodes nothing — audio plays but video stays black).
  requestKeyFrame(consumerId) {
    return this.rtcRequest('rtc:request-keyframe', { consumerId });
  }

  setOutputQuality(producerId, quality) {
    return this.rtcRequest('rtc:set-output-quality', { producerId, quality });
  }

  // Pause/resume one of MY own producers (the sidebar cam/mic track toggles).
  // The SFU stops/starts relaying it to watchers without tearing down consumers.
  pauseProducer(producerId) {
    return this.rtcRequest('rtc:pause-producer', { producerId });
  }

  resumeProducer(producerId) {
    return this.rtcRequest('rtc:resume-producer', { producerId });
  }

  setViewQuality(consumerId, quality) {
    return this.rtcRequest('rtc:set-view-quality', { consumerId, quality });
  }

  closeProducer(producerId) {
    return this.rtcRequest('rtc:close-producer', { producerId });
  }

  closeConsumer(consumerId) {
    return this.rtcRequest('rtc:close-consumer', { consumerId });
  }

  // Webcam request handshake: ask targetUserId to share their cam with me.
  // viaInvite tags this as the request fired by ACCEPTING targetUserId's cam
  // invite, so the publisher's client auto-accepts it (one-shot pre-acceptance)
  // rather than re-prompting. A plain cam-icon click omits it and is treated as
  // an ordinary request.
  requestWebcam(targetUserId, { viaInvite = false } = {}) {
    return this.rtcRequest('rtc:webcam-request', { targetUserId, viaInvite });
  }

  // Accept a webcam request from targetUserId (the original requester).
  acceptWebcam(targetUserId) {
    return this.rtcRequest('rtc:webcam-accept', { targetUserId });
  }

  // Tell targetUserId (the original requester) their request was QUEUED for my
  // manual approval — i.e. I'm in private mode. This is how the requester learns
  // the request is actually gated (vs. auto-accepted): my private/public mode
  // never leaves my client, so they show their "You requested…" notice only on
  // this ack instead of guessing at request time.
  queuedWebcam(targetUserId) {
    return this.rtcRequest('rtc:webcam-queued', { targetUserId });
  }

  // Deny a webcam request from targetUserId (the original requester).
  denyWebcam(targetUserId) {
    return this.rtcRequest('rtc:webcam-deny', { targetUserId });
  }

  // Cam-view invite (reverse of a request): I (a private publisher) invite
  // targetUserId to view MY cam. The server relays it to them; they see the
  // same accept/deny banner. Accepting sends back a viaInvite webcam-request
  // that I auto-accept.
  inviteWebcam(targetUserId) {
    return this.rtcRequest('rtc:webcam-invite', { targetUserId });
  }

  // Decline a cam invite from targetUserId (the inviting publisher).
  denyWebcamInvite(targetUserId) {
    return this.rtcRequest('rtc:webcam-invite-deny', { targetUserId });
  }

  // -------------------------------------------------------------------------
  // Peer-to-peer FILE TRANSFER. Direct user-to-user, brokered by the server
  // but NOT stored: an offer (metadata) -> accept/deny -> base64 chunk relay.
  // The sender's bytes are read locally, sliced, and streamed over the socket;
  // the server forwards them to the accepting receiver, which reassembles a
  // Blob. Mirrors the webcam request/accept/deny handshake.

  // Mirror of the backend cap (25 MB) so we reject oversized files before
  // bothering the recipient with an offer. Keep in sync with MAX_FILE_BYTES.
  static get MAX_FILE_BYTES() { return 25 * 1024 * 1024; }
  // Binary chunk size read from the File and base64-encoded per relay message.
  // 64 KB keeps each relayed payload comfortably under the server's per-chunk
  // cap while limiting the number of socket emits for a large file.
  static get FILE_CHUNK_BYTES() { return 64 * 1024; }

  // Send an OFFER to targetUserId. Metadata only — no bytes yet. The receiver
  // gets a banner; the actual transfer starts when they accept (the App layer
  // calls sendFileData on the file:accept event). transferId ties the offer,
  // accept, and chunks together; the caller generates and keeps it.
  offerFile(targetUserId, { transferId, name, size, mime }) {
    return this.rtcRequest('file:offer', { targetUserId, transferId, name, size, mime });
  }

  // Receiver accepts an offer from targetUserId (the sender). The server
  // authorizes this transferId for receipt and tells the sender to stream.
  acceptFile(targetUserId, transferId) {
    return this.rtcRequest('file:accept', { targetUserId, transferId });
  }

  // Receiver denies an offer from targetUserId (the sender).
  denyFile(targetUserId, transferId) {
    return this.rtcRequest('file:deny', { targetUserId, transferId });
  }

  // Either side aborts a transfer (sender cancels before/after accept, or
  // receiver cancels mid-stream). Best-effort relay; safe to call repeatedly.
  cancelFile(targetUserId, transferId, reason = 'cancelled') {
    if (!this.isConnected || !this.socket) return Promise.resolve({});
    return this.rtcRequest('file:cancel', { targetUserId, transferId, reason });
  }

  // Stream an accepted file's bytes to targetUserId as base64 chunks. Reads the
  // File incrementally (slice + FileReader) so a large file never sits fully in
  // memory twice. Calls onProgress(sentBytes/totalBytes) as it goes. Resolves
  // when the final chunk (last:true) has been emitted; rejects on read error.
  // The receiver knows it's done via the last flag — no per-chunk ack, so this
  // is fire-and-forward over the (ordered, reliable) socket.
  sendFileData(targetUserId, transferId, file, { onProgress } = {}) {
    return new Promise((resolve, reject) => {
      if (!this.isConnected || !this.socket) {
        return reject(new Error('Socket not connected'));
      }
      const chunkBytes = SocketService.FILE_CHUNK_BYTES;
      const total = file.size;
      let offset = 0;
      let seq = 0;

      const readNext = () => {
        if (offset >= total) {
          // Empty file edge case: nothing was sent, so emit a final empty
          // chunk so the receiver can finalize a 0-byte file.
          if (seq === 0) {
            this.socket.emit('file:chunk', {
              targetUserId, transferId, seq: 0, chunk: '', last: true,
            });
          }
          return resolve();
        }
        const slice = file.slice(offset, Math.min(offset + chunkBytes, total));
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error || new Error('File read failed'));
        reader.onload = () => {
          // reader.result is a data URL: "data:<mime>;base64,<payload>".
          // Strip the prefix to get the raw base64 the receiver will decode.
          const result = reader.result || '';
          const b64 = result.slice(result.indexOf(',') + 1);
          offset += slice.size;
          const last = offset >= total;
          this.socket.emit('file:chunk', { targetUserId, transferId, seq, chunk: b64, last });
          seq += 1;
          if (onProgress) onProgress(Math.min(offset, total), total);
          if (last) return resolve();
          // Yield to the event loop between chunks so a big file doesn't peg
          // the main thread or flood the socket buffer in one synchronous burst.
          setTimeout(readNext, 0);
        };
        reader.readAsDataURL(slice);
      };

      readNext();
    });
  }

  // Incoming file OFFER ({ fromUserId, fromUsername, transferId, name, size, mime }).
  onFileOffer(callback) {
    if (this.socket) this.socket.on('file:offer', callback);
  }

  // The receiver ACCEPTED my offer ({ fromUserId, fromUsername, transferId }) —
  // start streaming the bytes with sendFileData.
  onFileAccept(callback) {
    if (this.socket) this.socket.on('file:accept', callback);
  }

  // The receiver DENIED my offer ({ fromUserId, fromUsername, transferId }).
  onFileDeny(callback) {
    if (this.socket) this.socket.on('file:deny', callback);
  }

  // A relayed file CHUNK ({ fromUserId, transferId, seq, chunk(b64), last }).
  onFileChunk(callback) {
    if (this.socket) this.socket.on('file:chunk', callback);
  }

  // The other side CANCELLED/aborted ({ fromUserId, transferId, reason }).
  onFileCancel(callback) {
    if (this.socket) this.socket.on('file:cancel', callback);
  }

  // Publisher toggles whether watcherUserId may watch MY cam (the eye toggle).
  // allowed:false blocks + kicks them; allowed:true unblocks.
  setWatchPermission(watcherUserId, allowed) {
    return this.rtcRequest('rtc:set-watch-permission', { watcherUserId, allowed });
  }

  // ADMIN: kick targetUserId out of the room (ADMIN/OWNER only — server
  // re-checks). The server notifies the target ('kicked') then disconnects them.
  // Resolves with the server ack ({ ok } or rejects on { error }).
  kickUser(targetUserId) {
    return this.rtcRequest('kick', { targetUserId });
  }

  // ADMIN: permanently ban targetUserId from this workspace. Server sets
  // isBanned on their membership so socket reconnect and REST auth are refused.
  // Session-only alternative: use kickUser. No unban in v1 UI.
  banUser(targetUserId) {
    return this.rtcRequest('ban', { targetUserId });
  }

  // ADMIN: ban the IP address of targetUserId's current connection AND set
  // isBanned on their membership. Requires the target to be live in the room
  // (v1 only bans current-connection IPs). Resolves with { ok, ip }.
  banIpUser(targetUserId) {
    return this.rtcRequest('ban-ip', { targetUserId });
  }

  // I was kicked by an admin ({ by }). The server is about to disconnect me;
  // the App shows a "You have been kicked by X" modal and tears down the session.
  onKicked(callback) {
    if (this.socket) this.socket.on('kicked', callback);
  }

  // =====================================================================
  // AFK idle policy (see the idle sweep in backend socketService.js).
  // =====================================================================

  // Tell the server this user is present. Fire-and-forget with NO payload: the
  // server stamps its own clock, so a client cannot claim to have been active
  // and there is no clock skew to correct. Sent by useActivityPing.
  sendActivityPing() {
    if (this.isConnected && this.socket) this.socket.emit('presence:activity');
  }

  // "You've been idle; you'll be disconnected in graceMs." The client derives
  // its own deadline from that DURATION, never from a server timestamp.
  onIdleWarning(callback) {
    if (this.socket) this.socket.on('idle:warning', callback);
  }

  // Activity resumed (or the tenant disabled the policy) — drop the banner.
  // The server owns this decision so the client never has to guess whether its
  // affirmation landed.
  onIdleWarningCleared(callback) {
    if (this.socket) this.socket.on('idle:warning-cleared', callback);
  }

  // Disconnected for inactivity. Distinct from 'kicked' (an admin removed you):
  // the session is KEPT, so the App shows a Rejoin screen rather than bouncing
  // to the auth screen.
  onIdleKicked(callback) {
    if (this.socket) this.socket.on('idle:kicked', callback);
  }

  // =====================================================================
  // Friends v1 — request → approval handshake (see backend friend:* handlers).
  // All ack-style via rtcRequest (resolve on {ok}, reject on {error}); the two
  // on* helpers subscribe to server pushes, mirroring the onKicked style.
  // =====================================================================

  // Send a friend request to userId. Server may auto-accept if they already
  // requested me (resolves with { autoAccepted: true } in that case).
  requestFriend(userId) {
    return this.rtcRequest('friend:request', { userId });
  }

  // Respond to a PENDING request from userId (I'm the addressee).
  respondFriend(userId, accept) {
    return this.rtcRequest('friend:respond', { userId, accept });
  }

  // Cancel a PENDING request I sent to userId.
  cancelFriendRequest(userId) {
    return this.rtcRequest('friend:cancel', { userId });
  }

  // Remove an ACCEPTED friendship with userId (either side may call).
  removeFriend(userId) {
    return this.rtcRequest('friend:remove', { userId });
  }

  // Snapshot my friend graph: { friends:[ids], incoming:[ids], outgoing:[ids] }.
  getFriends() {
    return this.rtcRequest('friend:list');
  }

  // Someone sent ME a friend request ({ fromUserId, fromUsername }).
  onFriendRequestReceived(callback) {
    if (this.socket) this.socket.on('friend:request-received', callback);
  }

  // A request I sent was resolved ({ userId: <them>, accepted: bool }).
  onFriendRequestResolved(callback) {
    if (this.socket) this.socket.on('friend:request-resolved', callback);
  }

  // =====================================================================
  // Ignore / Mute v1 — private, client-enforced, server-persisted.
  // Ack-style via rtcRequest; server persists the list on my membership and
  // acks the fresh { ignoredUserIds }. No server push — the ignored user is
  // never told. Enforcement (hiding chat/IM/mention) lives in App.js/ChatPanel.
  // =====================================================================

  // Set/clear ignore for a user. Resolves with { ok, ignoredUserIds }.
  setIgnore(userId, ignored) {
    return this.rtcRequest('ignore:set', { userId, ignored });
  }

  // =====================================================================
  // Quick Replies — per-user saved composer snippets (modern chat71
  // "predefined messages"). Full-list replace via ack (list is tiny);
  // always re-render from the acked server list. `used` is fire-and-forget
  // analytics — never blocks an insert.
  // =====================================================================

  // Replace my whole quick-reply list. Resolves { ok, quickReplies }.
  setQuickReplies(quickReplies) {
    return this.rtcRequest('quickreply:set', { quickReplies });
  }

  // Count a snippet insertion (analytics only). Fire-and-forget.
  markQuickReplyUsed(id) {
    if (this.socket && this.socket.connected) {
      this.socket.emit('quickreply:used', { id });
    }
  }

  // =====================================================================
  // Group "Sesh" Countdown — sesh:* emit wrappers + subscription helpers.
  //
  // The server owns the clock (seshService): these emitters just request a
  // start/join and surface the ack; the on* helpers subscribe to the
  // server-broadcast phase/tick/smoker events that drive SeshBanner. Mirrors the
  // rtcRequest ack pattern (resolve on ok, reject on { error }).
  // =====================================================================

  /** Start a sesh for the current room (members only — server enforces). */
  startSesh() {
    return this.rtcRequest('sesh:start');
  }

  /** Opt into the live sesh (any user; only valid during the JOIN window). */
  joinSesh() {
    return this.rtcRequest('sesh:join');
  }

  /**
   * Stream a live head transform while YOU are the noodler, so watchers can pin
   * the jester hat to your head on your video tile. Fire-and-forget (no ack) —
   * it's a high-frequency relay like a tick. `t` is { cx, cy, wFrac, rollDeg }
   * (fractions of the video frame + degrees), or pass { gone: true } when the
   * face is lost so watchers hide the tracked hat. The server ignores it unless
   * you're actually the active noodler and rate-limits it.
   */
  sendNoodleTrack(t) {
    if (this.socket && this.socket.connected) this.socket.emit('sesh:noodle-track', t);
  }

  /**
   * Fetch a user's profile usage stats. Omit userId for your own. Resolves to
   * { ok, userId, stats } where stats is a flat { metric: value } map (empty {}
   * when the tenant's stats feature is off). Reuses the ack-promise helper.
   */
  getUserStats(userId) {
    return this.rtcRequest('get-user-stats', userId ? { userId } : {});
  }

  // A sesh STARTED in the room (phase=join). Payload is the full snapshot:
  // { phase, secondsLeft, total, smokers, initiator }.
  onSeshStart(callback) {
    if (this.socket) this.socket.on('sesh:start', callback);
  }

  // One-second clock tick during JOIN/PREP. Payload { phase, secondsLeft } —
  // the AUTHORITATIVE number (the client may interpolate the ring between ticks
  // but must take the displayed seconds from here).
  onSeshTick(callback) {
    if (this.socket) this.socket.on('sesh:tick', callback);
  }

  // Sesh entered the PREP phase (phase=prep, secondsLeft=10). Full snapshot.
  onSeshPrep(callback) {
    if (this.socket) this.socket.on('sesh:prep', callback);
  }

  // Live smoker list changed (someone joined). Payload { smokers }.
  onSeshSmokers(callback) {
    if (this.socket) this.socket.on('sesh:smokers', callback);
  }

  // TAKE A HIT — the countdown reached zero (phase=done). Full snapshot incl.
  // final smokers; the banner blinks for the short hit window, then the server
  // clears the sesh (no further events — fall back to idle).
  onSeshDone(callback) {
    if (this.socket) this.socket.on('sesh:done', callback);
  }

  // Full-state snapshot for THIS socket — sent on join-chat when a sesh is
  // already running so a late joiner renders the correct phase/time/smokers.
  onSeshState(callback) {
    if (this.socket) this.socket.on('sesh:state', callback);
  }

  // A user NOODLED — they started a sesh that landed as a near-miss. Payload
  // { userId, username, shameUntil } where shameUntil is the ms-epoch of the
  // 420/350 they fumbled toward. They wear a jester hat until then. Broadcast to
  // the room on the noodle, and re-sent to THIS socket on late join if still
  // active (so a fresh arrival mid-shame sees the hat too).
  onSeshNoodleShame(callback) {
    if (this.socket) this.socket.on('sesh:noodle-shame', callback);
  }

  // Live head transform for the current noodler (from THEIR browser, relayed by
  // the server). Payload { userId, cx, cy, wFrac, rollDeg } to position the hat,
  // or { userId, gone: true } when their face is momentarily lost. Only the
  // noodler emits these; watchers apply them to that user's video tile.
  onSeshNoodleTrack(callback) {
    if (this.socket) this.socket.on('sesh:noodle-track', callback);
  }

  // A new producer appeared in the room (someone enabled their webcam).
  onNewProducer(callback) {
    if (this.socket) this.socket.on('rtc:new-producer', callback);
  }

  // A producer went away (publisher disabled webcam / disconnected).
  onProducerClosed(callback) {
    if (this.socket) this.socket.on('rtc:producer-closed', callback);
  }

  // A publisher paused/resumed one of their producers (cam/mic toggle). Payload
  // { producerId, userId, kind, paused } — lets watchers show a placeholder
  // instead of a frozen frame.
  onProducerPaused(callback) {
    if (this.socket) this.socket.on('rtc:producer-paused', callback);
  }

  // Someone STARTED watching MY cam (drives the "they watch me" eye).
  onWatcherAdded(callback) {
    if (this.socket) this.socket.on('rtc:watcher-added', callback);
  }

  // Someone STOPPED watching MY cam.
  onWatcherRemoved(callback) {
    if (this.socket) this.socket.on('rtc:watcher-removed', callback);
  }

  // Someone is REQUESTING to watch my cam ({ fromUserId, fromUsername }).
  onWebcamRequest(callback) {
    if (this.socket) this.socket.on('rtc:webcam-request', callback);
  }

  // A private publisher QUEUED my webcam request for manual approval
  // ({ fromUserId, fromUsername }) — show the "You requested…" notice.
  onWebcamQueued(callback) {
    if (this.socket) this.socket.on('rtc:webcam-queued', callback);
  }

  // A user ACCEPTED my webcam request ({ fromUserId, fromUsername }) — safe to consume.
  onWebcamAccept(callback) {
    if (this.socket) this.socket.on('rtc:webcam-accept', callback);
  }

  // A publisher DENIED my webcam request ({ fromUserId, fromUsername }).
  onWebcamDeny(callback) {
    if (this.socket) this.socket.on('rtc:webcam-deny', callback);
  }

  // A publisher INVITED me to view their cam ({ fromUserId, fromUsername }) —
  // show the same accept/deny banner (flagged as an invite).
  onWebcamInvite(callback) {
    if (this.socket) this.socket.on('rtc:webcam-invite', callback);
  }

  // An invitee DECLINED my cam invite ({ fromUserId, fromUsername }).
  onWebcamInviteDeny(callback) {
    if (this.socket) this.socket.on('rtc:webcam-invite-deny', callback);
  }

  // A publisher BLOCKED me from their cam ({ ownerUserId }) — drop their tile
  // (a preceding rtc:producer-closed handles the consumer teardown) and hide
  // their watch button.
  onCamBlocked(callback) {
    if (this.socket) this.socket.on('rtc:cam-blocked', callback);
  }

  // A publisher UNBLOCKED me ({ ownerUserId }) — their watch button reappears.
  onCamUnblocked(callback) {
    if (this.socket) this.socket.on('rtc:cam-unblocked', callback);
  }

  /**
   * Send typing indicator
   */
  startTyping() {
    if (this.isConnected && this.socket) {
      this.socket.emit('typing-start');
    }
  }

  /**
   * Stop typing indicator
   */
  stopTyping() {
    if (this.isConnected && this.socket) {
      this.socket.emit('typing-stop');
    }
  }

  /**
   * Update user status
   * @param {string} status - User status
   * @param {string} statusMessage - Optional status message
   */
  updateStatus(status, statusMessage = '') {
    if (this.isConnected && this.socket) {
      this.socket.emit('user-status-change', {
        status,
        statusMessage
      });
    }
  }

  /**
   * Get available rooms
   */
  getRooms() {
    if (this.isConnected && this.socket) {
      this.socket.emit('get-rooms');
    }
  }

  /**
   * Legacy compatibility methods
   */
  sendLegacyMessage(content, type = 'TEXT') {
    if (this.isConnected && this.socket) {
      this.socket.emit('send', this.authData?.user || {}, content, { type });
    }
  }

  sendLegacyPrivate(content, targetUserId) {
    if (this.isConnected && this.socket) {
      this.socket.emit('sendPrivate', this.authData?.user || {}, content, targetUserId, {});
    }
  }

  // Event listeners
  onNewMessage(callback) {
    if (this.socket) {
      this.socket.on('new-message', callback);
    }
  }

  onUserJoined(callback) {
    if (this.socket) {
      this.socket.on('user-joined', callback);
    }
  }

  onUserLeft(callback) {
    if (this.socket) {
      this.socket.on('user-left', callback);
    }
  }

  onRoomUsers(callback) {
    if (this.socket) {
      this.socket.on('room-users', callback);
    }
  }

  // Ack to THIS user after their own set-avatar succeeds: { avatarUrl }.
  onAvatarUpdated(callback) {
    if (this.socket) {
      this.socket.on('avatar-updated', callback);
    }
  }

  // Another user in the room changed their avatar: { userId, avatar }.
  onUserAvatarUpdated(callback) {
    if (this.socket) {
      this.socket.on('user-avatar-updated', callback);
    }
  }

  onUserTyping(callback) {
    if (this.socket) {
      this.socket.on('user-typing', callback);
    }
  }

  onError(callback) {
    if (this.socket) {
      this.socket.on('error', callback);
    }
  }

  onDisplaced(callback) {
    if (this.socket) {
      this.socket.on('displaced', callback);
    }
  }

  // Enhanced event listeners
  onPrivateMessage(callback) {
    if (this.socket) {
      this.socket.on('receive-private', callback);
    }
  }

  onPrivateSent(callback) {
    if (this.socket) {
      this.socket.on('private-sent', callback);
    }
  }

  // A private-chat peer started/stopped typing at me (IM window typing line).
  onPrivateTyping(callback) {
    if (this.socket) {
      this.socket.on('private-typing', callback);
    }
  }

  onUserStatusChanged(callback) {
    if (this.socket) {
      this.socket.on('user-status-changed', callback);
    }
  }

  onRoomsReceived(callback) {
    if (this.socket) {
      this.socket.on('got-rooms', callback);
    }
  }

  // Legacy compatibility event listeners
  onReceiveText(callback) {
    if (this.socket) {
      this.socket.on('receiveText', callback);
      // Also listen to new format
      this.socket.on('new-message', callback);
    }
  }


  onAddUser(callback) {
    if (this.socket) {
      this.socket.on('addUser', callback);
      // Also listen to new format
      this.socket.on('user-joined', callback);
    }
  }

  onRemoveUser(callback) {
    if (this.socket) {
      this.socket.on('removeUser', callback);
      // Also listen to new format
      this.socket.on('user-left', callback);
    }
  }

  onUserChanged(callback) {
    if (this.socket) {
      this.socket.on('userChanged', callback);
      // Also listen to new format
      this.socket.on('user-status-changed', callback);
    }
  }

  onWrites(callback) {
    if (this.socket) {
      this.socket.on('writes', callback);
      // Also listen to new format
      this.socket.on('user-typing', callback);
    }
  }

  // Remove event listeners
  off(event, callback) {
    if (this.socket) {
      this.socket.off(event, callback);
    }
  }

  offAll() {
    if (this.socket) {
      // Modern events
      this.socket.off('new-message');
      this.socket.off('user-joined');
      this.socket.off('user-left');
      this.socket.off('room-users');
      this.socket.off('user-typing');
      this.socket.off('error');
      this.socket.off('displaced');
      this.socket.off('receive-private');
      this.socket.off('private-sent');
      this.socket.off('private-typing');
      this.socket.off('kicked');
      this.socket.off('idle:warning');
      this.socket.off('idle:warning-cleared');
      this.socket.off('idle:kicked');
      this.socket.off('user-status-changed');
      this.socket.off('got-rooms');

      // WebRTC signaling events
      this.socket.off('rtc:new-producer');
      this.socket.off('rtc:producer-closed');
      this.socket.off('rtc:watcher-added');
      this.socket.off('rtc:watcher-removed');
      this.socket.off('rtc:webcam-request');
      this.socket.off('rtc:webcam-queued');
      this.socket.off('rtc:webcam-accept');
      this.socket.off('rtc:webcam-deny');
      this.socket.off('rtc:webcam-invite');
      this.socket.off('rtc:webcam-invite-deny');
      this.socket.off('rtc:cam-blocked');
      this.socket.off('rtc:cam-unblocked');

      // Peer-to-peer file transfer events
      this.socket.off('file:offer');
      this.socket.off('file:accept');
      this.socket.off('file:deny');
      this.socket.off('file:chunk');
      this.socket.off('file:cancel');

      // Group "Sesh" Countdown events
      this.socket.off('sesh:start');
      this.socket.off('sesh:tick');
      this.socket.off('sesh:prep');
      this.socket.off('sesh:smokers');
      this.socket.off('sesh:done');
      this.socket.off('sesh:state');

      // Legacy compatibility events
      this.socket.off('receiveText');
      this.socket.off('addUser');
      this.socket.off('removeUser');
      this.socket.off('userChanged');
      this.socket.off('writes');
      this.socket.off('getUsers');
    }
  }
}

// Create singleton instance
const socketServiceInstance = new SocketService();

// Dev-only handle so the reconnect/backoff path can be exercised from the
// console / automated preview. No-op in production builds.
if (process.env.NODE_ENV !== 'production') {
  window.__socketService = socketServiceInstance;
}

// Export singleton instance
export default socketServiceInstance;